import { firstAliasForFeed } from "@/store/feedSelectors";
import { idx, store } from "@/store/store";

function stampAliasMetadata(feedIds: Iterable<string>): void {
  store.updateMany("feed", feedIds, (row) => {
    const alias = firstAliasForFeed(row.id);
    if (!alias) return row;
    return row.isAliasFeed && row.aliasMembershipId === alias.workspaceMembershipId
      ? row
      : { ...row, isAliasFeed: true, aliasMembershipId: alias.workspaceMembershipId };
  });
}

/** Marks every feed that has a command alias as an alias feed owned by that alias's membership. */
export async function initializeIsAliasFeed(): Promise<void> {
  const feedIds = new Set<string>();
  for (const alias of store.getAll("workspaceCommandAlias").values()) {
    if (alias.feedId != null) feedIds.add(alias.feedId);
  }
  stampAliasMetadata(feedIds);
}

export async function initializeIsAliasFeedFromIds(feedIds: string[]): Promise<void> {
  if (feedIds.length === 0) return;
  stampAliasMetadata(new Set(feedIds));
}

/** The feeds on which any of `membershipIds` owns a command alias. */
export function aliasFeedIdsOwnedBy(membershipIds: Iterable<string>): Set<string> {
  const feedIds = new Set<string>();
  for (const membershipId of membershipIds) {
    for (const key of idx.commandAliasesByMembership.keys(membershipId)) {
      const feedId = store.get("workspaceCommandAlias", key)?.feedId;
      if (feedId != null) feedIds.add(feedId);
    }
  }
  return feedIds;
}

/** Per-feed set of account ids owning a command alias on the feed (via their membership). */
function aliasAccountIdsForFeed(feedId: string): Set<string> {
  const accountIds = new Set<string>();
  for (const key of idx.commandAliasesByFeed.keys(feedId)) {
    const alias = store.get("workspaceCommandAlias", key);
    if (!alias) continue;
    const membership = store.get("workspaceMembership", alias.workspaceMembershipId);
    if (membership?.accountId != null) accountIds.add(membership.accountId);
  }
  return accountIds;
}

/**
 * Stamps `isFromAliasMember` on items authored by an alias owner of their feed (set-only).
 *
 * An item stored before its feed's alias (or the alias owner's membership) arrived was derived as not the driver's;
 * this re-derives it once they have.
 *
 * @param feedIds - Only these feeds' items, as a sync that touched their aliases names them; every item when omitted.
 */
export async function initializeFromAlias(feedIds?: Iterable<string>): Promise<void> {
  const aliasAccountsByFeed = new Map<string, Set<string>>();
  const itemIds = feedIds === undefined ? store.getAll("item").keys() : itemIdsOnFeeds(feedIds);
  store.updateMany("item", itemIds, (row) => {
    if (row.isFromAliasMember === true || row.accountId == null) return row;
    let aliasAccounts = aliasAccountsByFeed.get(row.feedId);
    if (!aliasAccounts) {
      aliasAccounts = aliasAccountIdsForFeed(row.feedId);
      aliasAccountsByFeed.set(row.feedId, aliasAccounts);
    }
    return aliasAccounts.has(row.accountId) ? { ...row, isFromAliasMember: true } : row;
  });
}

/** The ids of the stored items on `feedIds`. */
function itemIdsOnFeeds(feedIds: Iterable<string>): string[] {
  const ids: string[] = [];
  for (const feedId of feedIds) ids.push(...idx.itemsByFeed.keys(feedId));
  return ids;
}
